import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const store_id = searchParams.get('store_id') || '0';

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [validating, setValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (token) {
            axios.get(`${API}/customers/validate-reset-token?token=${token}&store_id=${store_id}`)
                .then(() => setTokenValid(true))
                .catch(() => setTokenValid(false))
                .finally(() => setValidating(false));
        } else {
            setValidating(false);
        }
    }, [token, store_id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            toast.error("Passwords do not match");
            return;
        }
        
        if (password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API}/customers/reset-password`, { token, store_id, password });
            setSuccess(true);
            toast.success("Password reset successful!");
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to reset password");
        } finally {
            setLoading(false);
        }
    };

    if (validating) {
        return (
            <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-amber-700 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFDFD] flex flex-col font-sans">
            <Header />

            <div className="flex-1 flex items-center justify-center p-4 mt-20">
                <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold text-slate-900">
                            {success ? "Password Reset!" : !token || !tokenValid ? "Invalid Link" : "Set New Password"}
                        </h1>
                    </div>

                    {success ? (
                        <div className="text-center space-y-6">
                            <p className="text-slate-600">Your password has been reset successfully. You can now log in.</p>
                            <Button
                                onClick={() => navigate('/customer/login')}
                                className="w-full h-11 rounded-xl font-bold bg-amber-700 hover:bg-amber-800 text-white"
                            >
                                Go to Login
                            </Button>
                        </div>
                    ) : !token || !tokenValid ? (
                        <div className="text-center space-y-6">
                            <p className="text-slate-600">This password reset link is invalid or has expired.</p>
                            <Button
                                onClick={() => navigate('/customer/forgot-password')}
                                className="w-full h-11 rounded-xl font-bold bg-amber-700 hover:bg-amber-800 text-white"
                            >
                                Request New Link
                            </Button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <Label>New Password</Label>
                                <Input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    className="h-11 rounded-xl"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Confirm Password</Label>
                                <Input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    className="h-11 rounded-xl"
                                />
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full h-11 rounded-xl font-bold bg-amber-700 hover:bg-amber-800 text-white"
                            >
                                {loading ? 'Resetting...' : 'Reset Password'}
                            </Button>
                        </form>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
}
